import React, { useState, useEffect } from "react";
import { FaCirclePlus, FaCircleMinus, FaPlus, FaMinus, FaArrowLeft } from "react-icons/fa6";
import CountrySelectInput from "../shared/countrySelectInput";
import TransportationMode from "./TransportationMode";
import Select from "react-select";
import { api, API_BASE_URL, API_DEPLOYED_URL } from "../../config/api";
import axios from "axios";
import { getAllSuperAdminUsers } from "../../lib/user";
import { Select as AntdSelect } from "antd"; // Import Antd Select for ECCN dropdown

const AcceptEstimationForm = ({ estimation, onClose, onSubmit }) => {
    const lockNonAddressSections = true;
    const lockedFieldClass = lockNonAddressSections ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "";
    const initialPcsPackage = estimation.dimensions && estimation.dimensions.length > 0
        ? estimation.dimensions.map(dim => ({
            quantity: dim.quantity?.value || dim.quantity || 1,
            weight: { value: dim.weight?.value || "", unit: dim.weight?.unit || "kg" },
            length: { value: dim.length?.value || "", unit: dim.length?.unit || "cm" },
            width: { value: dim.width?.value || "", unit: dim.width?.unit || "cm" },
            height: { value: dim.height?.value || "", unit: dim.height?.unit || "cm" },
        }))
        : [{ quantity: 1, weight: { value: "", unit: "kg" }, length: { value: "", unit: "cm" }, width: { value: "", unit: "cm" }, height: { value: "", unit: "cm" } }];

    const [formData, setFormData] = useState({
        loadName: estimation.userName || "",
        loadAddress: "",
        loadPostalCode: "",
        loadCity: estimation.originCity || "",
        loadCountry: estimation.originCountry || "",
        unloadName: "",
        unloadAddress: "",
        unloadPostalCode: "",
        unloadCity: estimation.destinationCity || "",
        unloadCountry: estimation.destinationCountry || "",
        pcsPackage: initialPcsPackage,
        items: estimation.items || [
            { itemName: "", quantity: 1, itemValue: "", countryOrigin: "", itemHSCode: "" },
        ],
        requiredDoc: estimation.requiredDoc || "not_Required",
        serviceType: estimation.selectedService || "",
        currency: estimation.itemsCurrency || "USD",
        pkgType: "pallet",
        eccnCode: estimation.eccnCode || "", // Add eccnCode to formData
        modeOfTransfer: "By Air",
    });

    const [loading, setLoading] = useState(false);
    const [eccnCodes, setEccnCodes] = useState([]); // State for ECCN codes

    const requiredFields = [
        "loadName",
        "loadAddress_CompanyName",
        "loadAddress",
        "loadPostalCode",
        "loadCity",
        "loadCountry",
        "unloadName",
        "unloadAddress_CompanyName",
        "unloadAddress",
        "unloadPostalCode",
        "unloadCity",
        "unloadCountry",
        "modeOfTransfer",
        "requiredDoc",
        "serviceType",
    ];

    const readOnlyFields = Object.keys(estimation).filter(
        (field) => estimation[field] && estimation[field] !== ""
    );

    // Fetch ECCN codes on component mount, similar to EstimateForm
    useEffect(() => {
        const fetchEccnCodes = async () => {
            try {
                const response = await api.get(`${API_BASE_URL}/eccn`);
                setEccnCodes(response.data);
            } catch (error) {
                console.error("Error fetching ECCN codes:", error);
            }
        };
        fetchEccnCodes();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handlePhoneNumberInput = (e) => {
        const { name, value } = e.target;
        if (value.length <= 20) {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handlePcsOfPkgInput = (index, field, e) => {
        const newPcsPackage = [...formData.pcsPackage];
        if (field === "quantity") {
            const value = parseInt(e.target.value);
            newPcsPackage[index][field] = isNaN(value) || value < 1 ? 1 : value;
        } else {
            newPcsPackage[index][field] = { ...newPcsPackage[index][field], value: e.target.value };
        }
        setFormData({ ...formData, pcsPackage: newPcsPackage });
    };

    const handleUnitChange = (index, field, e) => {
        const newPcsPackage = [...formData.pcsPackage];
        newPcsPackage[index][field] = { ...newPcsPackage[index][field], unit: e.target.value };
        setFormData({ ...formData, pcsPackage: newPcsPackage });
    };

    const handleAddMorePcsOfpackage = () => {
        setFormData({
            ...formData,
            pcsPackage: [
                ...formData.pcsPackage,
                { quantity: 1, weight: { value: "", unit: "kg" }, length: { value: "", unit: "cm" }, width: { value: "", unit: "cm" }, height: { value: "", unit: "cm" } },
            ],
        });
    };

    const handleRemovePcsOfpackage = () => {
        if (formData.pcsPackage.length > 1) {
            setFormData({ ...formData, pcsPackage: formData.pcsPackage.slice(0, -1) });
        }
    };

    const handleServiceInputChange = (index, field, e) => {
        const newItems = [...formData.items];
        newItems[index][field] = field === "quantity" ? parseInt(e.target.value) || 1 : e.target.value;
        setFormData({ ...formData, items: newItems });
    };

    const handleAntdServiceInputChange = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        setFormData({ ...formData, items: newItems });
    };

    const handleAddMore = () => {
        setFormData({
            ...formData,
            items: [
                ...formData.items,
                { itemName: "", quantity: 1, itemValue: "", countryOrigin: "", itemHSCode: "" },
            ],
        });
    };

    const handleRemoveService = (index) => {
        if (formData.items.length > 1) {
            setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
        }
    };

    const createAdminNotification = async (estimateData, estimateId) => {
        try {
            const itemNames = formData.items.map(item => item.itemName);
            const formattedItemNames = itemNames.join(', ');
            const notificationData = {
                title: "Estimation Accepted",
                message: `${formData.loadName} has accepted the estimation for ${formattedItemNames} and sent for final review.`,
                type: "estimation-accepted",
                link: `/complete-estimation/${estimateId}`,
                linkText: "View Accepted Estimation",
                role: "admin",
            };

            return api.post(`${API_BASE_URL}/notifications`, notificationData);
        } catch (error) {
            console.error("Error creating admin notification:", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const missingFields = requiredFields.filter((field) => !formData[field]);
        const invalidPackages = formData.pcsPackage.some(
            (pkg) => !pkg.quantity || pkg.quantity < 1 || !pkg.weight.value || !pkg.length.value || !pkg.width.value || !pkg.height.value
        );
        const invalidItems = formData.items.some(
            (item) => !item.itemName || !item.quantity || item.quantity < 1 || !item.itemValue || !item.countryOrigin || !item.itemHSCode
        );

        if (missingFields.length > 0 || invalidPackages || invalidItems) {
            let errorMessage = "Please fill all required fields:\n";
            if (missingFields.length > 0) errorMessage += `- Missing fields: ${missingFields.join(", ")}\n`;
            if (invalidPackages) errorMessage += "- Package dimensions are incomplete or invalid (e.g., Quantity)\n";
            if (invalidItems) errorMessage += "- Item details are incomplete or invalid (e.g., Quantity, Country of Origin)\n";
            alert(errorMessage);
            return;
        }

        try {
            setLoading(true);

            const formattedDimensions = formData.pcsPackage.map(pkg => ({
                quantity: { value: pkg.quantity },
                weight: pkg.weight,
                length: pkg.length,
                width: pkg.width,
                height: pkg.height
            }));

            const updatedEstimation = {
                ...estimation,
                userName: formData.loadName,
                userCity: formData.loadCity,
                postalCode: formData.loadPostalCode,
                originCountry: formData.loadCountry,
                loadAddress: formData.loadAddress,
                loadAddress_CompanyName: formData.loadAddress_CompanyName || "",
                loadAddress_ReferenceCustomer: formData.loadAddress_ReferenceCustomer || "",
                loadAddress_PhoneNumber: formData.loadAddress_PhoneNumber || "",
                unloadName: formData.unloadName,
                destinationCity: formData.unloadCity,
                destinationCountry: formData.unloadCountry,
                unloadAddress: formData.unloadAddress,
                unloadPostalCode: formData.unloadPostalCode,
                unloadAddress_CompanyName: formData.unloadAddress_CompanyName || "",
                unloadAddress_ReferenceCustomer: formData.unloadAddress_ReferenceCustomer || "",
                unloadAddress_PhoneNumber: formData.unloadAddress_PhoneNumber || "",
                modeOfTransfer: formData.modeOfTransfer,
                dimensions: formattedDimensions,
                items: formData.items,
                requiredDoc: formData.requiredDoc,
                selectedService: formData.serviceType,
                itemsCurrency: formData.currency,
                eccnCode: formData.eccnCode, // Include eccnCode in updatedEstimation
                estimationAccept: true,
            };

            // console.log("Sending update request with data:", JSON.stringify(updatedEstimation));

            const response = await api.put(`${API_BASE_URL}/clientestimates/id/${estimation.estimateId}`, updatedEstimation);

            const responseData = response.data;

            const adminsPromise = getAllSuperAdminUsers();
            const notificationPromise = createAdminNotification(updatedEstimation, estimation.estimateId);
            const [admins] = await Promise.all([adminsPromise, notificationPromise]);

            const adminEmails = admins.map((admin) => admin.email);
            const link = `${API_DEPLOYED_URL}/estimated-ior/${estimation.estimateId}`;
            await api.post(`${API_BASE_URL}/mail/estimate-notification`, {
                estimateData: updatedEstimation,
                isUpdate: true,
                adminEmails,
                link,
            });

            onSubmit(responseData.data || responseData);
            onClose();

        } catch (error) {
            console.error("Error updating estimation:", error);
            alert(`Error occurred while updating estimation: ${error.message || error.response?.data?.message || "Unknown error"}`);
        } finally {
            setLoading(false);
        }
    };

    // Render ECCN option similar to EstimateForm
    const renderEccnOption = (eccn) => (
        <div className="flex flex-col py-2 px-3 hover:bg-blue-50 transition-colors">
            <div className="flex justify-between items-center">
                <span className="font-semibold text-blue-600">{eccn.eccn_code}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${eccn.restrictions ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                    {eccn.restrictions ? 'Restricted' : 'Unrestricted'}
                </span>
            </div>
            <span className="text-sm text-gray-600 mt-1">{eccn.description}</span>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-4 rounded-md w-full max-w-5xl overflow-y-auto z-50" style={{ maxHeight: 'calc(90vh - 64px)' }}>
                <div className="w-full border border-red">
                    <div className="p-4 bg-cus-gray">
                        <div className="mb-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-full md:w-auto flex rounded-[5px] items-center space-x-4 py-2 px-4 text-white btnBg"
                            >
                                <FaArrowLeft className="mr-2" /> Back
                            </button>
                        </div>
                        <div className="p-4 mx-auto bg-white border rounded-md">
                            <h2 className="text-xl font-bold mb-4">Complete the Form to Accept Estimation</h2>
                            <form onSubmit={handleSubmit}>
                                {/* Collection Address */}
                                <h2 className="mx-[30px] font-bold text-xl mt-4">Collection address</h2>
                                <div className="mx-[30px] grid md:grid-cols-3 grid-cols-1 gap-3">
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="loadName">
                                            <span className="text-red-500">*</span> Name
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            id="loadName"
                                            name="loadName"
                                            onChange={handleInputChange}
                                            value={formData.loadName}
                                            readOnly={readOnlyFields.includes("userName")}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="loadAddress_CompanyName">
                                            <span className="text-red-500">*</span> Company Name
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Company Name"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            name="loadAddress_CompanyName"
                                            id="loadAddress_CompanyName"
                                            onChange={handleInputChange}
                                            value={formData.loadAddress_CompanyName || ""}
                                            required
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="loadAddress_ReferenceCustomer">
                                            <span className="text-red-500">*</span> Reference Customer
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            id="loadAddress_ReferenceCustomer"
                                            name="loadAddress_ReferenceCustomer"
                                            onChange={handleInputChange}
                                            value={formData.loadAddress_ReferenceCustomer || ""}
                                        />
                                    </div>
                                </div>
                                <div className="mx-[30px] grid md:grid-cols-3 grid-cols-1 gap-3">
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="loadAddress">
                                            <span className="text-red-500">*</span> Address
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Address"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            name="loadAddress"
                                            id="loadAddress"
                                            onChange={handleInputChange}
                                            value={formData.loadAddress}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="loadPostalCode">
                                            <span className="text-red-500">*</span> Zip Code
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Zip Code"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            name="loadPostalCode"
                                            id="loadPostalCode"
                                            onChange={handleInputChange}
                                            value={formData.loadPostalCode}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="loadCity">
                                            <span className="text-red-500">*</span> City
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="city name"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            id="loadCity"
                                            name="loadCity"
                                            onChange={handleInputChange}
                                            value={formData.loadCity}
                                            readOnly={readOnlyFields.includes("userCity")}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="loadCountry">
                                            <span className="text-red-500">*</span> Load Country
                                        </label>
                                        <CountrySelectInput
                                            label=""
                                            onChange={handleInputChange}
                                            value={formData.loadCountry}
                                            name="loadCountry"
                                            id="loadCountry"
                                            disabled={readOnlyFields.includes("originCountry")}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="loadAddress_PhoneNumber">
                                            Mobile Number
                                        </label>
                                        <input
                                            type="tel"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            name="loadAddress_PhoneNumber"
                                            id="loadAddress_PhoneNumber"
                                            onChange={handlePhoneNumberInput}
                                            value={formData.loadAddress_PhoneNumber || ""}
                                            maxLength="20"
                                        />
                                    </div>
                                </div>

                                {/* Delivery Address */}
                                <h2 className="mx-[30px] font-bold text-xl mt-4">Delivery address</h2>
                                <div className="mx-[30px] grid md:grid-cols-2 grid-cols-1 gap-3">
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="unloadName">
                                            <span className="text-red-500">*</span> Name
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            id="unloadName"
                                            name="unloadName"
                                            onChange={handleInputChange}
                                            value={formData.unloadName}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="unloadAddress_CompanyName">
                                            <span className="text-red-500">*</span> Company Name
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Company Name"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            name="unloadAddress_CompanyName"
                                            id="unloadAddress_CompanyName"
                                            onChange={handleInputChange}
                                            value={formData.unloadAddress_CompanyName || ""}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="mx-[30px] grid md:grid-cols-3 grid-cols-1 gap-3">
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="unloadAddress">
                                            <span className="text-red-500">*</span> Address
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Address"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            name="unloadAddress"
                                            id="unloadAddress"
                                            onChange={handleInputChange}
                                            value={formData.unloadAddress}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="unloadPostalCode">
                                            <span className="text-red-500">*</span> Zip Code
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Zip Code"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            name="unloadPostalCode"
                                            id="unloadPostalCode"
                                            onChange={handleInputChange}
                                            value={formData.unloadPostalCode}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="unloadCity">
                                            <span className="text-red-500">*</span> City
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="city name"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            id="unloadCity"
                                            name="unloadCity"
                                            onChange={handleInputChange}
                                            value={formData.unloadCity}
                                            readOnly={readOnlyFields.includes("destinationCity")}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="unloadCountry">
                                            <span className="text-red-500">*</span> Country
                                        </label>
                                        <CountrySelectInput
                                            label=""
                                            onChange={handleInputChange}
                                            value={formData.unloadCountry}
                                            name="unloadCountry"
                                            id="unloadCountry"
                                            disabled={readOnlyFields.includes("destinationCountry")}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="unloadAddress_PhoneNumber">
                                            Mobile Number
                                        </label>
                                        <input
                                            type="tel"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            name="unloadAddress_PhoneNumber"
                                            id="unloadAddress_PhoneNumber"
                                            onChange={handlePhoneNumberInput}
                                            value={formData.unloadAddress_PhoneNumber || ""}
                                            maxLength="20"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="unloadAddress_ReferenceCustomer">
                                            <span className="text-red-500">*</span> Reference Customer
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                            id="unloadAddress_ReferenceCustomer"
                                            name="unloadAddress_ReferenceCustomer"
                                            onChange={handleInputChange}
                                            value={formData.unloadAddress_ReferenceCustomer || ""}
                                        />
                                    </div>
                                </div>

                                {/* Mode of Transport */}
                                <div className="mx-[30px]">
                                    <div className="flex flex-col gap-1 py-2">
                                        <label className="w-full text-sm font-normal" htmlFor="modeOfTransfer">
                                            <span className="text-red-500">*</span> Mode of Transport
                                        </label>
                                        <TransportationMode
                                            formData={formData}
                                            setFormData={setFormData}
                                            disabled={true}
                                            defaultMode="By Air"
                                        />
                                    </div>
                                </div>

                                {/* Package Details */}
                                {formData.pcsPackage.map((formItem, index) => (
                                    <div
                                        key={`pcsPackage-${index}`}
                                        className="mx-[30px] flex-wrap justify-between flex items-center gap-5"
                                    >
                                        <div className="w-full lg:w-[10%] flex flex-col gap-1 py-2">
                                            <label className="w-full text-sm font-normal" htmlFor={`pcsPackage-quantity-${index}`}>
                                                Quantity <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                className={`w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                id={`pcsPackage-quantity-${index}`}
                                                onChange={(e) => handlePcsOfPkgInput(index, "quantity", e)}
                                                onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                                value={formItem.quantity || 1}
                                                min="1"
                                                required
                                                readOnly={lockNonAddressSections || (readOnlyFields.includes("dimensions") && estimation.dimensions[index]?.quantity)}
                                            />
                                        </div>
                                        <div className="flex w-full lg:w-1/5 flex-col gap-1 py-2">
                                            <label className="w-full text-sm font-normal" htmlFor={`weight-${index}`}>
                                                Weight<span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex items-center">
                                                <input
                                                    type="number"
                                                    className={`w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    name={`weight-${index}`}
                                                    id={`weight-${index}`}
                                                    onChange={(e) => handlePcsOfPkgInput(index, "weight", e)}
                                                    onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                                    value={formItem.weight.value}
                                                    required
                                                    readOnly={lockNonAddressSections || (readOnlyFields.includes("dimensions") && estimation.dimensions[index]?.weight.value)}
                                                />
                                                <select
                                                    className={`ml-2 p-1 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    name={`weight-unit-${index}`}
                                                    id={`weight-unit-${index}`}
                                                    onChange={(e) => handleUnitChange(index, "weight", e)}
                                                    value={formItem.weight.unit}
                                                    disabled={lockNonAddressSections || (readOnlyFields.includes("dimensions") && estimation.dimensions[index]?.weight.unit)}
                                                >
                                                    <option value="kg">kg</option>
                                                    <option value="lbs">lbs</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex w-full lg:w-1/5 flex-col gap-1 py-2">
                                            <label className="w-full text-sm font-normal" htmlFor={`length-${index}`}>
                                                Length<span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex items-center">
                                                <input
                                                    type="number"
                                                    className={`w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    name={`length-${index}`}
                                                    id={`length-${index}`}
                                                    onChange={(e) => handlePcsOfPkgInput(index, "length", e)}
                                                    onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                                    value={formItem.length.value}
                                                    required
                                                    readOnly={lockNonAddressSections || (readOnlyFields.includes("dimensions") && estimation.dimensions[index]?.length.value)}
                                                />
                                                <select
                                                    className={`ml-2 p-1 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    name={`length-unit-${index}`}
                                                    id={`length-unit-${index}`}
                                                    onChange={(e) => handleUnitChange(index, "length", e)}
                                                    value={formItem.length.unit}
                                                    disabled={lockNonAddressSections || (readOnlyFields.includes("dimensions") && estimation.dimensions[index]?.length.unit)}
                                                >
                                                    <option value="cm">cm</option>
                                                    <option value="in">in</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex w-full lg:w-1/5 flex-col gap-1 py-2">
                                            <label className="w-full text-sm font-normal" htmlFor={`width-${index}`}>
                                                Width<span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex items-center">
                                                <input
                                                    type="number"
                                                    className={`w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    name={`width-${index}`}
                                                    id={`width-${index}`}
                                                    onChange={(e) => handlePcsOfPkgInput(index, "width", e)}
                                                    onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                                    value={formItem.width.value}
                                                    required
                                                    readOnly={lockNonAddressSections || (readOnlyFields.includes("dimensions") && estimation.dimensions[index]?.width.value)}
                                                />
                                                <select
                                                    className={`ml-2 p-1 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    name={`width-unit-${index}`}
                                                    id={`width-unit-${index}`}
                                                    onChange={(e) => handleUnitChange(index, "width", e)}
                                                    value={formItem.width.unit}
                                                    disabled={lockNonAddressSections || (readOnlyFields.includes("dimensions") && estimation.dimensions[index]?.width.unit)}
                                                >
                                                    <option value="cm">cm</option>
                                                    <option value="in">in</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex w-full lg:w-1/5 flex-col gap-1 py-2">
                                            <label className="w-full text-sm font-normal" htmlFor={`height-${index}`}>
                                                Height<span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex items-center">
                                                <input
                                                    type="number"
                                                    className={`w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    name={`height-${index}`}
                                                    id={`height-${index}`}
                                                    onChange={(e) => handlePcsOfPkgInput(index, "height", e)}
                                                    onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                                    value={formItem.height.value}
                                                    required
                                                    readOnly={lockNonAddressSections || (readOnlyFields.includes("dimensions") && estimation.dimensions[index]?.height.value)}
                                                />
                                                <select
                                                    className={`ml-2 p-1 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    name={`height-unit-${index}`}
                                                    id={`height-unit-${index}`}
                                                    onChange={(e) => handleUnitChange(index, "height", e)}
                                                    value={formItem.height.unit}
                                                    disabled={lockNonAddressSections || (readOnlyFields.includes("dimensions") && estimation.dimensions[index]?.height.unit)}
                                                >
                                                    <option value="cm">cm</option>
                                                    <option value="in">in</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {!lockNonAddressSections && (
                                    <div className="flex justify-between gap-5 items-center px-5 py-2">
                                        <div className="flex gap-1 items-center">
                                            <div className="mx-[20px]" onClick={handleAddMorePcsOfpackage}>
                                                <FaPlus className="text-[20px] text-[#03336E]" />
                                            </div>
                                            <div className="mx-[5px]" onClick={handleRemovePcsOfpackage}>
                                                <FaMinus className="text-[20px] text-[#03336E]" />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Package Type */}
                                <div className="flex flex-col md:w-1/2 py-3 gap-4 px-5">
                                    <label className="w-full text-sm font-normal" htmlFor="pkgType">
                                        Select Package Type
                                    </label>
                                    <select
                                        className="w-full p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                        name="pkgType"
                                        id="pkgType"
                                        onChange={handleInputChange}
                                        value={formData.pkgType}
                                    >
                                        <option value="pallet">Pallet</option>
                                        <option value="box">Box</option>
                                        <option value="container">Container</option>
                                    </select>
                                </div>

                                {/* Item Details */}
                                <div className="flex gap-5 items-center mt-8">
                                    <div className="mx-[30px] flex items-center gap-4">
                                        <h2 className="text-xl font-bold">Item details</h2>
                                        {!lockNonAddressSections && (
                                            <FaCirclePlus className="text-[22px] text-[#03336e]" onClick={handleAddMore} />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <h1 className="mt-2">Currency</h1>
                                        <select
                                            className={`ml-2 p-1 w-full text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                            name="currency"
                                            id="currency"
                                            onChange={handleInputChange}
                                            value={formData.currency}
                                            disabled={lockNonAddressSections || readOnlyFields.includes("itemsCurrency")}
                                        >
                                            <option value="USD">USD</option>
                                            <option value="EUR">EUR</option>
                                            <option value="GBP">GBP</option>
                                        </select>
                                    </div>
                                </div>
                                {formData.items.map((formItem, index) => (
                                    <div key={`item-${index}`} className="md:px-10">
                                        <h2 className="flex gap-3 items-center text-[16px] font-bold text-[gray] mt-6">
                                            Item details
                                            {!lockNonAddressSections && (
                                                <FaCircleMinus onClick={() => handleRemoveService(index)} />
                                            )}
                                        </h2>
                                        <div className="grid md:grid-cols-2 grid-cols-1 gap-3">
                                            <div className="flex flex-col gap-1 py-2">
                                                <label className="w-full text-sm font-normal" htmlFor={`itemName-${index}`}>
                                                    <span className="text-red-500">*</span> Item Name
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    id={`itemName-${index}`}
                                                    onChange={(e) => handleServiceInputChange(index, "itemName", e)}
                                                    value={formItem.itemName}
                                                    required
                                                    readOnly={lockNonAddressSections || (readOnlyFields.includes("items") && estimation.items[index]?.itemName)}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-1 py-2">
                                                <label className="w-full text-sm font-normal" htmlFor={`item-quantity-${index}`}>
                                                    <span className="text-red-500">*</span> Quantity
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    id={`item-quantity-${index}`}
                                                    onChange={(e) => handleServiceInputChange(index, "quantity", e)}
                                                    onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                                    value={formItem.quantity}
                                                    min="1"
                                                    required
                                                    readOnly={lockNonAddressSections || (readOnlyFields.includes("items") && estimation.items[index]?.quantity)}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-1 py-2">
                                                <label className="w-full text-sm font-normal" htmlFor={`itemValue-${index}`}>
                                                    <span className="text-red-500">*</span> Item Value
                                                </label>
                                                <input
                                                    type="number"
                                                    className={`w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    id={`itemValue-${index}`}
                                                    onChange={(e) => handleServiceInputChange(index, "itemValue", e)}
                                                    onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                                    value={formItem.itemValue}
                                                    required
                                                    readOnly={lockNonAddressSections || (readOnlyFields.includes("items") && estimation.items[index]?.itemValue)}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-1 py-2">
                                                <label className="w-full text-sm font-normal" htmlFor={`countryOrigin-${index}`}>
                                                    <span className="text-red-500">*</span> Country of Origin
                                                </label>
                                                <CountrySelectInput
                                                    label=""
                                                    onChange={(e) => handleAntdServiceInputChange(index, "countryOrigin", e.target.value)}
                                                    value={formItem.countryOrigin}
                                                    name={`countryOrigin-${index}`}
                                                    id={`countryOrigin-${index}`}
                                                    disabled={lockNonAddressSections || (readOnlyFields.includes("items") && estimation.items[index]?.countryOrigin)}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-1 py-2">
                                                <label className="w-full text-sm font-normal" htmlFor={`itemHSCode-${index}`}>
                                                    <span className="text-red-500">*</span> Item HS Code
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                                    id={`itemHSCode-${index}`}
                                                    onChange={(e) => handleServiceInputChange(index, "itemHSCode", e)}
                                                    value={formItem.itemHSCode}
                                                    required
                                                    readOnly={lockNonAddressSections || (readOnlyFields.includes("items") && estimation.items[index]?.itemHSCode)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Conditional Rendering for Import Documentation and ECCN Code */}
                                <div className="flex flex-col px-5 gap-4 mt-4"> {/* Increased gap for better spacing */}
                                    {(formData.serviceType === "IOR" || formData.serviceType === "EOR & IOR") && (
                                        <>
                                            <label className="block mb-2 text-lg font-normal text-black">
                                                <span className="text-red-500">*</span> Import documentation required?
                                            </label>
                                            <div className={`flex items-center gap-6 ${lockNonAddressSections ? "text-gray-500" : ""}`}> {/* Improved alignment and spacing */}
                                                <label className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="requiredDoc"
                                                        value="Required"
                                                        onChange={handleInputChange}
                                                        checked={formData.requiredDoc === "Required"}
                                                        disabled={lockNonAddressSections || readOnlyFields.includes("requiredDoc")}
                                                        className="mr-2" // Added margin for spacing
                                                    />
                                                    <span className="text-sm">Required</span>
                                                </label>
                                                <label className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="requiredDoc"
                                                        value="not_Required"
                                                        onChange={handleInputChange}
                                                        checked={formData.requiredDoc === "not_Required"}
                                                        disabled={lockNonAddressSections || readOnlyFields.includes("requiredDoc")}
                                                        className="mr-2" // Added margin for spacing
                                                    />
                                                    <span className="text-sm">Not Required</span>
                                                </label>
                                            </div>
                                        </>
                                    )}
                                    {(formData.serviceType === "EOR" || formData.serviceType === "EOR & IOR") && (
                                        <div className="flex flex-col gap-1 py-2 w-full md:w-1/2"> {/* Adjusted width to 50% on medium screens */}
                                            <label className="w-full text-sm font-normal" htmlFor="eccnCode">
                                                <span className="text-red-500">*</span> ECCN Code
                                            </label>
                                            <AntdSelect
                                                showSearch
                                                placeholder="Select ECCN Code"
                                                value={formData.eccnCode}
                                                disabled={true}
                                                style={{ width: "100%" }} // Maintains full width within the wrapper
                                                optionLabelProp="label"
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    option.children.props.children.some(child =>
                                                        child.toString().toLowerCase().includes(input.toLowerCase())
                                                    )
                                                }
                                                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                                                className="text-sm" // Consistent text size
                                            >
                                                {eccnCodes.map((eccn) => (
                                                    <AntdSelect.Option
                                                        key={eccn.eccn_code}
                                                        value={eccn.eccn_code}
                                                        label={eccn.eccn_code}
                                                    >
                                                        {renderEccnOption(eccn)}
                                                    </AntdSelect.Option>
                                                ))}
                                            </AntdSelect>
                                        </div>
                                    )}
                                </div>

                                {/* Services Dropdown - Updated Styling with Consistent Padding */}
                                <div className="flex flex-col gap-1 py-2 px-5"> {/* Added px-5 for left padding */}
                                    <label className="w-full text-sm font-normal" htmlFor="serviceType">
                                        <span className="text-red-500">*</span> Services
                                    </label>
                                    <select
                                        name="serviceType"
                                        id="serviceType"
                                        onChange={handleInputChange}
                                        value={formData.serviceType}
                                        className={`w-full md:w-1/2 h-8 p-1 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${lockedFieldClass}`}
                                        disabled={lockNonAddressSections || readOnlyFields.includes("selectedService")}
                                    >
                                        <option value="">Select</option>
                                        <option value="EOR">EOR</option>
                                        <option value="IOR">IOR</option>
                                        <option value="EOR & IOR">EOR & IOR</option>
                                        {formData.serviceType && !["EOR", "IOR", "EOR & IOR"].includes(formData.serviceType) && (
                                            <option value={formData.serviceType}>{formData.serviceType}</option>
                                        )}
                                    </select>
                                </div>

                                {/* Submit Button */}
                                <div className="flex justify-center items-center md:justify-start md:mx-[30px] mt-4">
                                    <button
                                        type="submit"
                                        className="bg-cus-blue rounded-md text-white p-2 text-sm md:w-[341px] w-[180px] disabled:bg-opacity-50"
                                        disabled={loading}
                                    >
                                        {loading ? "Submitting..." : "Accept Estimation"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AcceptEstimationForm;